import { Components, Theme, switchClasses } from '@mui/material';
import { Palette, switchSizes } from './constants';
import { Spacing2, Spacing3, baseSpace } from '../spacing';

const SvgClearPath = 'M18.3 5.70997C17.91 5.31997 17.28 5.31997 16.89 5.70997L12 10.59L7.11 5.69997C6.72 5.30997 6.09 5.30997 5.7 5.69997C5.31 6.08997 5.31 6.71997 5.7 7.10997L10.59 12L5.7 16.89C5.31 17.28 5.31 17.91 5.7 18.3C6.09 18.69 6.72 18.69 7.11 18.3L12 13.41L16.89 18.3C17.28 18.69 17.91 18.69 18.3 18.3C18.69 17.91 18.69 17.28 18.3 16.89L13.41 12L18.3 7.10997C18.68 6.72997 18.68 6.08997 18.3 5.70997V5.70997Z';
const SvgCheckPath = 'M9 16.1698L5.53 12.6998C5.14 12.3098 4.51 12.3098 4.12 12.6998C3.73 13.0898 3.73 13.7198 4.12 14.1098L8.3 18.2898C8.69 18.6798 9.32 18.6798 9.71 18.2898L20.29 7.70982C20.68 7.31982 20.68 6.68982 20.29 6.29982C19.9 5.90982 19.27 5.90982 18.88 6.29982L9 16.1698Z';

const inlineSvg = (path: string, color: string) => {
  const encodedColor = encodeURIComponent(color);
  return `url('data:image/svg+xml;utf8,<svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="${path}" fill="${encodedColor}" stroke="${encodedColor}" stroke-width="1" /></svg>')`;
};

export const MuiSwitch: Components<Theme>['MuiSwitch'] = {
  defaultProps: {
    color: 'secondary',
    size: 'medium',
    disabled: false,
    disableFocusRipple: true,
    focusRipple: false,
    disableRipple: true,
    centerRipple: false,
    disableTouchRipple: true,
    colorful: false,
  },
  styleOverrides: {
    root: ({ ownerState }) => ({
      boxSizing: 'border-box',
      padding: Spacing2,
      paddingRight: Spacing3,
      height: switchSizes[ownerState.size!] + 2 * baseSpace,
      width: switchSizes[ownerState.size!] * 2 + 2 * baseSpace + 4,

      [`&.${switchClasses.disabled}`]: {
        [`.${switchClasses.switchBase}.${switchClasses.disabled}`]: {
          [`+.${switchClasses.track}`]: {
            backgroundColor: Palette.track.disabled,
            borderColor: Palette.border.disabled,
            opacity: 1,
          },
          [`.${switchClasses.thumb}`]: {
            backgroundColor: Palette.track.disabled,
            borderColor: Palette.border.disabled,
            opacity: 1,
          },
        },
      },
    }),
    switchBase: ({ ownerState }) => ({
      boxSizing: 'border-box',
      height: switchSizes[ownerState.size!] + 2 * baseSpace,
      width: switchSizes[ownerState.size!] + 2 * baseSpace,
      padding: Spacing2,
      [`&.${switchClasses.checked}`]: {
        transform: `translateX(${switchSizes[ownerState.size!]}px)`,
        '&:hover': {
          background: 'transparent',
        },
      },
      '&:hover': {
        background: 'transparent',
      },
    }),
    track: ({ ownerState }) => ({
      boxSizing: 'border-box',
      borderRadius: switchSizes[ownerState.size!],
      height: switchSizes[ownerState.size!],
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: Palette.border.base,
      boxShadow: 'none',
      opacity: 1,
      [`.${switchClasses.switchBase}.${switchClasses.checked}+&`]: {
        opacity: 1,
      },
    }),
    thumb: ({ ownerState }) => ({
      boxSizing: 'border-box',
      borderRadius: switchSizes[ownerState.size!],
      width: switchSizes[ownerState.size!],
      height: switchSizes[ownerState.size!],
      background: Palette.thumb.background,
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: Palette.border.base,
      boxShadow: 'none',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      ...(ownerState.size === 'medium' && {
        backgroundImage: inlineSvg(SvgClearPath, Palette.icon.base),
        [`.${switchClasses.checked} &`]: {
          backgroundImage: inlineSvg(SvgCheckPath, Palette.icon.base),
        },
        [`.${switchClasses.disabled} &`]: {
          backgroundImage: inlineSvg(SvgClearPath, Palette.icon.disabled),
        },
        [`.${switchClasses.checked}.${switchClasses.disabled} &`]: {
          backgroundImage: inlineSvg(SvgCheckPath, Palette.icon.disabled),
        },
      }),
    }),
  },
  variants: [
    {
      props: {
        colorful: false,
        disabled: false,
      },
      style: {
        [`.${switchClasses.track}`]: {
          backgroundColor: Palette.track.base.unchecked,
        },
        [`.${switchClasses.switchBase}.${switchClasses.checked}+.${switchClasses.track}`]:
        {
          backgroundColor: Palette.track.base.checked,
          opacity: 1,
        },
      },
    },
    {
      props: {
        colorful: true,
        disabled: false,
      },
      style: {
        [`.${switchClasses.track}`]: {
          backgroundColor: Palette.track.colorful.unchecked,
        },
        [`.${switchClasses.switchBase}.${switchClasses.checked}+.${switchClasses.track}`]:
        {
          backgroundColor: Palette.track.colorful.checked,
          opacity: 1,
        },
      },
    },
  ],
};
